import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Upload,
  Image as ImageIcon,
  FileText as FileTextIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  Info,
  ScrollText,
  Package,
  Layout,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api } from "../../lib/api";
import { invalidateContent } from "../../lib/content";

/**
 * Fase 4 · CMS — pantalla admin de edición de contenidos por página.
 * Sidebar con las 7 páginas + panel dinámico según la seleccionada.
 */

const PAGES = [
  { key: "home", label: "Home", icon: HomeIcon },
  { key: "envio", label: "Tienda Envío", icon: StoreIcon },
  { key: "local", label: "Tienda Local", icon: StoreIcon },
  { key: "about", label: "Sobre nosotros", icon: Info },
  { key: "shipping_terms", label: "Condiciones de envío", icon: ScrollText },
  { key: "shipment_wizard", label: "Enviar paquete", icon: Package },
  { key: "footer", label: "Footer", icon: Layout },
];

const SEO_MAX = 160;

function formatDT(iso) {
  if (!iso) return "Sin cambios registrados";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}

// ---- Reusable atomic inputs ------------------------------------------------
function TextField({ label, name, value, onChange, placeholder, maxLength, hint }) {
  return (
    <div>
      <Label htmlFor={`f-${name}`} className="text-sm">{label}</Label>
      <Input
        id={`f-${name}`}
        data-testid={`admin-content-field-${name}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 h-11"
      />
      {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}

function TextAreaField({ label, name, value, onChange, placeholder, rows = 4, hint }) {
  return (
    <div>
      <Label htmlFor={`f-${name}`} className="text-sm">{label}</Label>
      <Textarea
        id={`f-${name}`}
        data-testid={`admin-content-field-${name}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="mt-1"
      />
      {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}

function SeoDescriptionField({ value, onChange }) {
  const len = (value || "").length;
  const over = len > SEO_MAX;
  return (
    <div>
      <Label htmlFor="f-seo_description" className="text-sm">SEO description</Label>
      <Textarea
        id="f-seo_description"
        data-testid="admin-content-field-seo_description"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className={`mt-1 ${over ? "border-red-400" : ""}`}
      />
      <div
        data-testid="admin-content-seo-char-count"
        className={`mt-1 text-xs ${over ? "text-red-600 font-bold" : "text-neutral-500"}`}
      >
        {len} / {SEO_MAX} caracteres{over ? " · demasiado largo" : ""}
      </div>
    </div>
  );
}

function ImageUploadField({ label, name, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen supera los 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo subir";
      toast.error(typeof msg === "string" ? msg : "Error al subir");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1 flex flex-col sm:flex-row gap-3">
        <Input
          data-testid={`admin-content-field-${name}`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="h-11 flex-1"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid="admin-content-image-upload-btn"
          className="rounded-full whitespace-nowrap"
        >
          <Upload className="h-4 w-4 mr-1" />
          {uploading ? "Subiendo…" : "Subir imagen"}
        </Button>
      </div>
      {value && (
        <div className="mt-2">
          <img
            data-testid="admin-content-image-preview"
            src={value.startsWith("http") ? value : `${window.location.origin}${value}`}
            alt="preview"
            className="max-h-40 rounded-xl border border-neutral-200 object-contain bg-white"
          />
        </div>
      )}
    </div>
  );
}

// ---- Array editors ---------------------------------------------------------
function FaqItemsEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const add = () => onChange([...items, { q: "", a: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-bold">FAQs (1–10)</Label>
        <Button
          type="button"
          size="sm"
          onClick={add}
          disabled={items.length >= 10}
          data-testid="admin-content-add-faq-btn"
          className="rounded-full"
          variant="outline"
        >
          <Plus className="h-4 w-4 mr-1" /> Añadir
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div
            key={i}
            className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-neutral-500">
                FAQ {i + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
                data-testid={`admin-content-remove-faq-${i}`}
                className="h-7 w-7 p-0 text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Input
              value={it.q || ""}
              onChange={(e) => update(i, { q: e.target.value })}
              placeholder="Pregunta"
              className="h-10 bg-white"
              data-testid={`admin-content-faq-q-${i}`}
            />
            <Textarea
              value={it.a || ""}
              onChange={(e) => update(i, { a: e.target.value })}
              placeholder="Respuesta"
              rows={2}
              className="bg-white"
              data-testid={`admin-content-faq-a-${i}`}
            />
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-neutral-500 italic px-1">
            Aún no hay FAQs. Añade la primera.
          </div>
        )}
      </div>
    </div>
  );
}

function SocialLinksEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const add = () => onChange([...items, { platform: "instagram", url: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, patch) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-bold">Redes sociales</Label>
        <Button
          type="button"
          size="sm"
          onClick={add}
          variant="outline"
          className="rounded-full"
          data-testid="admin-content-add-social-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Añadir
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <Select
              value={it.platform || "instagram"}
              onValueChange={(v) => update(i, { platform: v })}
            >
              <SelectTrigger
                className="h-10 sm:w-40"
                data-testid={`admin-content-social-platform-${i}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="x">X (Twitter)</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={it.url || ""}
              onChange={(e) => update(i, { url: e.target.value })}
              placeholder="https://…"
              className="h-10 flex-1"
              data-testid={`admin-content-social-url-${i}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              data-testid={`admin-content-remove-social-${i}`}
              className="h-10 w-10 p-0 text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-neutral-500 italic px-1">
            Sin redes sociales configuradas.
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Formularios por página ------------------------------------------------
function HomeForm({ fields, setField }) {
  return (
    <div className="space-y-5">
      <TextField label="Hero — título" name="hero_title" value={fields.hero_title} onChange={(v) => setField("hero_title", v)} placeholder="Bienvenidos a MercaGo" />
      <TextAreaField label="Hero — subtítulo" name="hero_subtitle" value={fields.hero_subtitle} onChange={(v) => setField("hero_subtitle", v)} rows={3} />
      <ImageUploadField label="Hero — imagen" name="hero_image_url" value={fields.hero_image_url} onChange={(v) => setField("hero_image_url", v)} />
      <TextField label="Hero — CTA (texto del botón)" name="hero_cta_label" value={fields.hero_cta_label} onChange={(v) => setField("hero_cta_label", v)} placeholder="Empezar ahora" maxLength={40} />
      <div className="pt-3 border-t border-neutral-200">
        <TextField label="SEO title" name="seo_title" value={fields.seo_title} onChange={(v) => setField("seo_title", v)} maxLength={80} />
        <div className="mt-4"><SeoDescriptionField value={fields.seo_description} onChange={(v) => setField("seo_description", v)} /></div>
      </div>
    </div>
  );
}

function ShopForm({ fields, setField }) {
  return (
    <div className="space-y-5">
      <TextField label="Banner — título" name="banner_title" value={fields.banner_title} onChange={(v) => setField("banner_title", v)} />
      <TextAreaField label="Banner — subtítulo" name="banner_subtitle" value={fields.banner_subtitle} onChange={(v) => setField("banner_subtitle", v)} rows={3} />
      <ImageUploadField label="Banner — imagen" name="banner_image_url" value={fields.banner_image_url} onChange={(v) => setField("banner_image_url", v)} />
      <div className="pt-3 border-t border-neutral-200">
        <TextField label="SEO title" name="seo_title" value={fields.seo_title} onChange={(v) => setField("seo_title", v)} maxLength={80} />
        <div className="mt-4"><SeoDescriptionField value={fields.seo_description} onChange={(v) => setField("seo_description", v)} /></div>
      </div>
    </div>
  );
}

function AboutLikeForm({ fields, setField }) {
  return (
    <div className="space-y-5">
      <TextField label="Título" name="title" value={fields.title} onChange={(v) => setField("title", v)} />
      <TextAreaField label="Cuerpo (párrafos separados por línea en blanco)" name="body_md" value={fields.body_md} onChange={(v) => setField("body_md", v)} rows={12} hint="Sin HTML raw. Los saltos de línea dobles crean párrafos separados." />
      <div className="pt-3 border-t border-neutral-200">
        <TextField label="SEO title" name="seo_title" value={fields.seo_title} onChange={(v) => setField("seo_title", v)} maxLength={80} />
        <div className="mt-4"><SeoDescriptionField value={fields.seo_description} onChange={(v) => setField("seo_description", v)} /></div>
      </div>
    </div>
  );
}

function WizardForm({ fields, setField }) {
  return (
    <div className="space-y-5">
      <TextField label="Intro — título" name="intro_title" value={fields.intro_title} onChange={(v) => setField("intro_title", v)} />
      <TextAreaField label="Intro — subtítulo" name="intro_subtitle" value={fields.intro_subtitle} onChange={(v) => setField("intro_subtitle", v)} rows={3} />
      <FaqItemsEditor value={fields.faq_items} onChange={(v) => setField("faq_items", v)} />
      <div className="pt-3 border-t border-neutral-200">
        <TextField label="SEO title" name="seo_title" value={fields.seo_title} onChange={(v) => setField("seo_title", v)} maxLength={80} />
        <div className="mt-4"><SeoDescriptionField value={fields.seo_description} onChange={(v) => setField("seo_description", v)} /></div>
      </div>
    </div>
  );
}

function FooterForm({ fields, setField }) {
  return (
    <div className="space-y-5">
      <TextAreaField label="Texto sobre MercaGo" name="about_text" value={fields.about_text} onChange={(v) => setField("about_text", v)} rows={3} />
      <TextField label="Email de contacto" name="contact_email" value={fields.contact_email} onChange={(v) => setField("contact_email", v)} placeholder="info@mercago.one" />
      <TextField label="Teléfono" name="contact_phone" value={fields.contact_phone} onChange={(v) => setField("contact_phone", v)} placeholder="+34 600 000 000" />
      <TextField label="Enlace WhatsApp" name="whatsapp_link" value={fields.whatsapp_link} onChange={(v) => setField("whatsapp_link", v)} placeholder="https://wa.me/34600000000" />
      <SocialLinksEditor value={fields.social_links} onChange={(v) => setField("social_links", v)} />
    </div>
  );
}

const FORM_BY_PAGE = {
  home: HomeForm,
  envio: ShopForm,
  local: ShopForm,
  about: AboutLikeForm,
  shipping_terms: AboutLikeForm,
  shipment_wizard: WizardForm,
  footer: FooterForm,
};

// ---- Página principal ------------------------------------------------------
export default function AdminContent() {
  const [selected, setSelected] = useState("home");
  const [doc, setDoc] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/content/${key}`);
      setDoc(data);
      setFields(data?.fields || {});
    } catch (_e) {
      toast.error("No se pudo cargar el contenido");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(selected);
  }, [selected, load]);

  const setField = (name, value) =>
    setFields((f) => ({ ...f, [name]: value }));

  const dirty = useMemo(() => {
    if (!doc) return false;
    return JSON.stringify(doc.fields) !== JSON.stringify(fields);
  }, [doc, fields]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/content/${selected}`, { fields });
      setDoc(data);
      setFields(data.fields || {});
      invalidateContent(selected);
      toast.success("Contenido guardado");
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo guardar";
      toast.error(typeof msg === "string" ? msg : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    setResetOpen(false);
    try {
      const { data } = await api.post(`/admin/content/${selected}/reset`);
      setDoc(data);
      setFields(data.fields || {});
      invalidateContent(selected);
      toast.success("Valores por defecto restaurados");
    } catch (_e) {
      toast.error("No se pudo restaurar");
    }
  };

  const Form = FORM_BY_PAGE[selected] || HomeForm;

  return (
    <div data-testid="admin-content-page" className="grid md:grid-cols-[220px_1fr] gap-6">
      {/* Sidebar de páginas */}
      <aside className="rounded-2xl border border-neutral-200 bg-white p-2">
        <div className="px-3 py-2 text-xs uppercase tracking-wider text-neutral-500">
          Páginas
        </div>
        <nav className="flex flex-col gap-1">
          {PAGES.map((p) => {
            const Icon = p.icon;
            const active = p.key === selected;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelected(p.key)}
                data-testid={`admin-content-nav-${p.key}`}
                className={`flex items-center gap-2 text-sm text-left px-3 py-2 rounded-lg ${
                  active
                    ? "bg-emerald-50 text-emerald-700 font-semibold"
                    : "text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                <Icon className="h-4 w-4" />
                {p.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Panel formulario */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
              <FileTextIcon className="h-6 w-6 text-emerald-600" />
              {PAGES.find((p) => p.key === selected)?.label}
            </h1>
            <div
              data-testid="admin-content-updated-meta"
              className="mt-1 text-xs text-neutral-500"
            >
              Última actualización: <b>{formatDT(doc?.updated_at)}</b>
              {doc?.updated_by ? <> · por <b>{doc.updated_by}</b></> : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => setResetOpen(true)}
              disabled={loading}
              data-testid="admin-content-reset-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Restaurar defaults
            </Button>
            <Button
              type="button"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="admin-content-save-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="h-40 animate-pulse rounded-xl bg-neutral-100" />
        ) : (
          <Form fields={fields} setField={setField} />
        )}
      </section>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar valores por defecto</AlertDialogTitle>
            <AlertDialogDescription>
              Se sobrescribirán todos los campos de esta página con los
              valores originales del seed. Esta acción no puede deshacerse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doReset} className="bg-red-600 hover:bg-red-700">
              Restaurar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
